import React, { useState, useEffect } from 'react';
import { getHomepageSettings, saveHomepageSettings, uploadFile } from '../../../services/contentApi';
import Icon from '../../../components/AppIcon';

const HomepageManager = () => {
  const [homepageData, setHomepageData] = useState({
    hero: {
      title: { line1: '', line2: '' },
      subtitle: '',
      ctaLabel: '',
      ctaHref: '',
      ctaHrefSecondary: '',
      backgroundUrl: ''
    },
    slides: [],
    sections: {
      services: { heading: '', subheading: '' },
      projects: { heading: '', subheading: '' },
      blogs: { heading: '', subheading: '' }
    },
    cta: {
      badge: '',
      headlineLine1: '',
      headlineLine2: '',
      subheadline: '',
      buttonPrimary: '',
      buttonSecondary: ''
    },
    footer: {
      companyDescription: '',
      contactInfo: { address: '', phone: '', email: '', hours: '' },
      socialLinks: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [savedData, setSavedData] = useState(null);
  const [uploadingSlideIndex, setUploadingSlideIndex] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSlideModal, setShowSlideModal] = useState(false);
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [slideFormData, setSlideFormData] = useState({
    imageUrl: '/assets/images/no_image.png',
    caption: '',
    linkHref: ''
  });

  // Detect unsaved changes by comparing serialized state
  const hasChanges = savedData !== null && JSON.stringify(homepageData) !== JSON.stringify(savedData);

  useEffect(() => {
    loadHomepageSettings();
  }, []);

  const loadHomepageSettings = async () => {
    try {
      const data = await getHomepageSettings();
      if (data) {
        // If footer data is missing from Firestore, seed it with the default values
        if (!data.footer) {
          const mergedData = {
            ...data,
            footer: {
              companyDescription: "Pune's definitive construction intelligence platform, transforming complex bureaucratic processes into streamlined digital experiences.",
              contactInfo: {
                address: 'Pune, Maharashtra, India',
                phone: '+91 80039 89393',
                email: 'nirmitee.associates2919@gmail.com',
                hours: 'Mon-Sat: 9:00 AM - 7:00 PM'
              },
              socialLinks: []
            }
          };
          setHomepageData(mergedData);
          await saveHomepageSettings(mergedData);
          setSavedData(JSON.parse(JSON.stringify(mergedData)));
        } else {
          // Backwards compat: migrate old object format to array format
          const existingSocial = data.footer.socialLinks;
          if (existingSocial && !Array.isArray(existingSocial)) {
            const iconMap = { facebook: 'Facebook', twitter: 'Twitter', linkedin: 'Linkedin', instagram: 'Instagram', youtube: 'Youtube' };
            const migratedLinks = Object.entries(existingSocial)
              .filter(([, href]) => href)
              .map(([key, href], i) => ({ id: Date.now() + i, label: key.charAt(0).toUpperCase() + key.slice(1), icon: iconMap[key] || key, href }));
            data.footer.socialLinks = migratedLinks;
          }
          setHomepageData(data);
          setSavedData(JSON.parse(JSON.stringify(data)));
        }
      }
    } catch (error) {
      console.error('Error loading homepage settings:', error);
      setMessage('Error loading homepage settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await saveHomepageSettings(homepageData);
      setSavedData(JSON.parse(JSON.stringify(homepageData)));
      setMessage('Homepage settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving homepage settings:', error);
      setMessage('Error saving homepage settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (path, value) => {
    setHomepageData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // --- Social Links CRUD ---
  const handleAddSocialLink = () => {
    setHomepageData(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        socialLinks: [...(prev.footer?.socialLinks || []), { id: Date.now(), label: '', icon: '', href: '' }]
      }
    }));
  };

  const handleUpdateSocialLink = (id, field, value) => {
    setHomepageData(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        socialLinks: (prev.footer?.socialLinks || []).map(link =>
          link.id === id ? { ...link, [field]: value } : link
        )
      }
    }));
  };

  const handleDeleteSocialLink = (id) => {
    setHomepageData(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        socialLinks: (prev.footer?.socialLinks || []).filter(link => link.id !== id)
      }
    }));
  };

  const handleOpenSlideModal = (index = null) => {
    if (index !== null) {
      setEditingSlideIndex(index);
      setSlideFormData({ ...homepageData.slides[index] });
    } else {
      setEditingSlideIndex(null);
      setSlideFormData({
        imageUrl: '/assets/images/no_image.png',
        caption: '',
        linkHref: ''
      });
    }
    setShowSlideModal(true);
  };

  const handleCloseSlideModal = () => {
    setShowSlideModal(false);
    setEditingSlideIndex(null);
    setUploadProgress(0);
  };

  const handleSlideFormSubmit = (e) => {
    e.preventDefault();
    setHomepageData(prev => {
      const newSlides = [...prev.slides];
      if (editingSlideIndex !== null) {
        newSlides[editingSlideIndex] = { ...slideFormData, order: newSlides[editingSlideIndex].order };
      } else {
        newSlides.push({ ...slideFormData, order: newSlides.length + 1 });
      }
      return { ...prev, slides: newSlides };
    });
    handleCloseSlideModal();
  };

  const removeSlide = (index) => {
    setHomepageData(prev => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== index)
    }));
  };

  const updateSlide = (index, field, value) => {
    setHomepageData(prev => ({
      ...prev,
      slides: prev.slides.map((slide, i) =>
        i === index ? { ...slide, [field]: value } : slide
      )
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingSlideIndex('modal'); // Use a special key for modal upload
      const result = await uploadFile(file, (progress) => {
        setUploadProgress(progress);
      });
      setSlideFormData(prev => ({ ...prev, imageUrl: result.url }));
    } catch (error) {
      console.error("Upload failed:", error);
      alert(error.message || "Failed to upload image.");
    } finally {
      setUploadingSlideIndex(null);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Homepage Management</h2>
            <p className="text-gray-600">Manage hero section, slideshow, and section headings</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded-lg ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}

        {/* Hero Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Section</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title Line 1</label>
              <input
                type="text"
                value={homepageData.hero.title.line1}
                onChange={(e) => handleInputChange('hero.title.line1', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Construction clarity in a"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title Line 2</label>
              <input
                type="text"
                value={homepageData.hero.title.line2}
                onChange={(e) => handleInputChange('hero.title.line2', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="complex world"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <textarea
                value={homepageData.hero.subtitle}
                onChange={(e) => handleInputChange('hero.subtitle', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Transform bureaucratic nightmares into streamlined success..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary CTA Label</label>
              <input
                type="text"
                value={homepageData.hero.ctaLabel}
                onChange={(e) => handleInputChange('hero.ctaLabel', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Book Free Consultation"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary CTA Link</label>
              <input
                type="text"
                value={homepageData.hero.ctaHrefSecondary}
                onChange={(e) => handleInputChange('hero.ctaHrefSecondary', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="#calculator"
              />
            </div>
          </div>
        </div>

        {/* Slideshow */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Slideshow Images</h3>
            <button
              onClick={() => handleOpenSlideModal()}
              className="bg-custom-brown text-white px-3 py-1 rounded text-sm hover:bg-custom-brown/90 transition-colors duration-200 flex items-center space-x-1"
            >
              <Icon name="Plus" size={16} />
              <span>Add Slide</span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {homepageData.slides.map((slide, index) => (
              <div key={index} className="group relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 hover:shadow-md transition-shadow">
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={slide.imageUrl || '/assets/images/no_image.png'}
                    alt={slide.caption}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                    <button
                      onClick={() => handleOpenSlideModal(index)}
                      className="p-2 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                      title="Edit Slide"
                    >
                      <Icon name="Edit" size={18} />
                    </button>
                    <button
                      onClick={() => removeSlide(index)}
                      className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      title="Remove Slide"
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-900 truncate">{slide.caption || 'No caption'}</p>
                  <p className="text-xs text-gray-500 truncate">{slide.imageUrl}</p>
                </div>
              </div>
            ))}
            {homepageData.slides.length === 0 && (
              <div className="col-span-full py-12 text-center border-2 border-dashed border-gray-200 rounded-lg">
                <Icon name="Image" size={48} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No slides added yet. Click "Add Slide" to begin.</p>
              </div>
            )}
          </div>
        </div>

        {/* Section Headings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Headings</h3>
          <div className="space-y-4">
            {Object.entries(homepageData.sections).map(([sectionKey, sectionData]) => (
              <div key={sectionKey} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 capitalize">{sectionKey}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
                    <input
                      type="text"
                      value={sectionData.heading}
                      onChange={(e) => handleInputChange(`sections.${sectionKey}.heading`, e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subheading</label>
                    <input
                      type="text"
                      value={sectionData.subheading}
                      onChange={(e) => handleInputChange(`sections.${sectionKey}.subheading`, e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CTA Section</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
              <input
                type="text"
                value={homepageData.cta?.badge || ''}
                onChange={(e) => handleInputChange('cta.badge', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Ready to Transform Your Project?"
              />
            </div>
            <div></div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Headline Line 1</label>
              <input
                type="text"
                value={homepageData.cta?.headlineLine1 || ''}
                onChange={(e) => handleInputChange('cta.headlineLine1', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Start Your Construction Journey with"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Headline Line 2</label>
              <input
                type="text"
                value={homepageData.cta?.headlineLine2 || ''}
                onChange={(e) => handleInputChange('cta.headlineLine2', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Confidence Today"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline</label>
              <textarea
                value={homepageData.cta?.subheadline || ''}
                onChange={(e) => handleInputChange('cta.subheadline', e.target.value)}
                rows="2"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Join 500+ successful projects in Pune. Get expert guidance..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
              <input
                type="text"
                value={homepageData.cta?.buttonPrimary || ''}
                onChange={(e) => handleInputChange('cta.buttonPrimary', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Book Free Consultation"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
              <input
                type="text"
                value={homepageData.cta?.buttonSecondary || ''}
                onChange={(e) => handleInputChange('cta.buttonSecondary', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="View Our Projects"
              />
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Footer Section</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Description</label>
              <textarea
                value={homepageData.footer?.companyDescription || ''}
                onChange={(e) => handleInputChange('footer.companyDescription', e.target.value)}
                rows="2"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Pune's definitive construction intelligence platform..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={homepageData.footer?.contactInfo?.address || ''}
                onChange={(e) => handleInputChange('footer.contactInfo.address', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Pune, Maharashtra, India"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={homepageData.footer?.contactInfo?.phone || ''}
                onChange={(e) => handleInputChange('footer.contactInfo.phone', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="+91 80039 89393"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                value={homepageData.footer?.contactInfo?.email || ''}
                onChange={(e) => handleInputChange('footer.contactInfo.email', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="nirmitee.associates2919@gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours</label>
              <input
                type="text"
                value={homepageData.footer?.contactInfo?.hours || ''}
                onChange={(e) => handleInputChange('footer.contactInfo.hours', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                placeholder="Mon-Sat: 9:00 AM - 7:00 PM"
              />
            </div>
            <div className="md:col-span-2 pt-2 border-t border-gray-200 mt-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Social Links</h4>
                <button
                  type="button"
                  onClick={handleAddSocialLink}
                  className="text-xs bg-custom-brown text-white px-3 py-1.5 rounded-md hover:bg-custom-brown/90 transition-colors"
                >
                  + Add Social Link
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Use Lucide icon names (e.g. Facebook, Instagram, Linkedin, Twitter, Youtube, MessageCircle)</p>
            </div>

            <div className="md:col-span-2 space-y-3">
              {(homepageData.footer?.socialLinks || []).length === 0 && (
                <p className="text-sm text-gray-400 italic">No social links added yet. Click "+ Add Social Link" above.</p>
              )}
              {(homepageData.footer?.socialLinks || []).map((link) => (
                <div key={link.id} className="grid grid-cols-3 gap-2 items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Label</label>
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => handleUpdateSocialLink(link.id, 'label', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-custom-brown"
                      placeholder="e.g. Instagram"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Icon Name</label>
                    <input
                      type="text"
                      value={link.icon}
                      onChange={(e) => handleUpdateSocialLink(link.id, 'icon', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-custom-brown"
                      placeholder="e.g. Instagram"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">URL</label>
                      <input
                        type="text"
                        value={link.href}
                        onChange={(e) => handleUpdateSocialLink(link.id, 'href', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-custom-brown"
                        placeholder="https://..."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteSocialLink(link.id)}
                      className="mb-0.5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Slide Modal */}
        {showSlideModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-200 animate-in fade-in zoom-in duration-200">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingSlideIndex !== null ? 'Edit Slide' : 'Add New Slide'}
                </h3>
                <button
                  onClick={handleCloseSlideModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Icon name="X" size={20} className="text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSlideFormSubmit} className="p-6 space-y-5">
                <div className="space-y-4">
                  {/* Preview & Upload */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 font-semibold">Slide Image</label>
                    <div className="relative aspect-video w-full bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 shadow-inner group">
                      <img
                        src={slideFormData.imageUrl || '/assets/images/no_image.png'}
                        alt="Preview"
                        className="w-full h-full object-cover transition-opacity duration-300"
                        onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
                      />

                      {/* Progress Overlay */}
                      {uploadingSlideIndex === 'modal' && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white space-y-3">
                          <div className="relative w-24 h-24 flex items-center justify-center">
                            <svg className="w-full h-full transform -rotate-90">
                              <circle
                                cx="48"
                                cy="48"
                                r="40"
                                stroke="currentColor"
                                strokeWidth="8"
                                fill="transparent"
                                className="text-white/20"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="40"
                                stroke="currentColor"
                                strokeWidth="8"
                                fill="transparent"
                                strokeDasharray="251.2"
                                strokeDashoffset={251.2 - (251.2 * uploadProgress) / 100}
                                className="text-custom-brown transition-all duration-300"
                              />
                            </svg>
                            <span className="absolute text-lg font-bold">{uploadProgress}%</span>
                          </div>
                          <p className="text-sm font-medium animate-pulse">Uploading Image...</p>
                        </div>
                      )}

                      {/* Hover Upload Button */}
                      {uploadingSlideIndex !== 'modal' && (
                        <label className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-all cursor-pointer group-hover:opacity-100 opacity-0">
                          <div className="bg-white p-3 rounded-full shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                            <Icon name="Upload" size={24} className="text-custom-brown" />
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploadingSlideIndex !== null}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (Optional)</label>
                      <input
                        type="text"
                        value={slideFormData.imageUrl}
                        onChange={(e) => setSlideFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-brown focus:border-custom-brown outline-none transition-all"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 uppercase tracking-wider text-xs">Slide Caption</label>
                      <input
                        type="text"
                        value={slideFormData.caption}
                        onChange={(e) => setSlideFormData(prev => ({ ...prev, caption: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-brown focus:border-custom-brown outline-none transition-all"
                        placeholder="Enter slide caption"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 flex items-center justify-end space-x-3 border-t border-gray-100 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseSlideModal}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingSlideIndex !== null}
                    className="px-6 py-2.5 bg-custom-brown text-white text-sm font-bold rounded-lg hover:shadow-lg active:scale-95 transition-all disabled:opacity-50 disabled:scale-100"
                  >
                    {editingSlideIndex !== null ? 'Save Changes' : 'Add Slide'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Sticky floating save bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl border border-white/10">
          <span className="text-sm font-medium flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block" />
            Unsaved changes
          </span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-custom-brown text-white text-sm font-semibold px-5 py-1.5 rounded-full hover:bg-custom-brown/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Now'}
          </button>
        </div>
      )}
    </>
  );
};

export default HomepageManager;
